import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiPostJson as apiPost } from '../../../../../../utils/api';
import CandidateRow from './CandidateRow';

export default function CandidatesTable({ candidates }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(() => new Set());
  const invalidate = () => qc.invalidateQueries({ queryKey: ['service-comp-candidates'] });

  // Drop any selected ids that are no longer in the current view (filter change,
  // refetch) so a batch invite can't target rows the admin can't see. Keeps still-
  // visible selections intact, unlike a blanket reset on every refetch.
  useEffect(() => {
    setSelected((s) => {
      const next = new Set([...s].filter((id) => candidates.some((c) => c.id === id)));
      return next.size === s.size ? s : next;
    });
  }, [candidates]);

  const review = useMutation({
    mutationFn: ({ id, action }) => apiPost(`/admin/service_comp_candidates/${id}/${action}`),
    onSuccess: (_d, v) => { toast.success(v.action === 'approve' ? 'Approved' : 'Denied'); invalidate(); },
    onError: () => toast.error('Action failed'),
  });

  // Selectable = anything still actionable (pending or approved).
  const selectableIds = candidates.filter((c) => ['pending', 'approved'].includes(c.status)).map((c) => c.id);
  const selPending = candidates.filter((c) => c.status === 'pending' && selected.has(c.id)).map((c) => c.id);
  const selApproved = candidates.filter((c) => c.status === 'approved' && selected.has(c.id)).map((c) => c.id);
  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));

  const batchApprove = async () => {
    try {
      const res = await apiPost('/admin/service_comp_candidates/batch_approve', { ids: selPending });
      toast.success(`Approved ${res.approved}`);
      setSelected(new Set());
      invalidate();
    } catch (e) {
      toast.error('Batch approve failed');
    }
  };

  const batchInvite = async () => {
    const missing = candidates.filter((c) => selApproved.includes(c.id) && !c.contact_email).length;
    if (missing && !window.confirm(`Invite ${selApproved.length} clients? ${missing} have no email and will be skipped.`)) return;
    try {
      const res = await apiPost('/admin/service_comp_candidates/batch_invite', { ids: selApproved });
      toast.success(`Invited ${res.invited.length}; skipped ${res.skipped.length}`);
      setSelected(new Set());
      invalidate();
    } catch (e) {
      toast.error('Batch invite failed');
    }
  };

  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-200">
          <tr>
            <th className="p-2"><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all" /></th>
            <th className="p-2">Company</th><th className="p-2">Contact</th><th className="p-2">State</th><th className="p-2">Last activity</th>
            <th className="p-2">Last login</th><th className="p-2">Umbrella</th>
            <th className="p-2">Status</th><th className="p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((c) => (
            <CandidateRow
              key={c.id}
              candidate={c}
              selected={selected.has(c.id)}
              onToggle={() => toggle(c.id)}
              onApprove={() => review.mutate({ id: c.id, action: 'approve' })}
              onDeny={() => review.mutate({ id: c.id, action: 'deny' })}
              onInvited={invalidate}
            />
          ))}
        </tbody>
      </table>

      {selected.size > 0 && (
        <div className="flex items-center justify-between mt-3 p-3 bg-blue-50 dark:bg-gray-800 rounded">
          <span className="text-sm dark:text-gray-200">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            {selPending.length > 0 && (
              <button onClick={batchApprove} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                ✓ Approve {selPending.length}
              </button>
            )}
            {selApproved.length > 0 && (
              <button onClick={batchInvite} className="px-3 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm">
                ✉ Invite {selApproved.length}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
